import { capitalize, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BudgetProposalWithResourcesT } from 'routes/BudgetModule/config/types';
import GenericAutocomplete from 'ui/DropdownItems/GenericAutocomplete';
import ResetWrapper from 'ui/DropdownItems/ResetWrapper';
import {
    AutocompletAble,
    formatValueForAutocomplete,
    ModalFormItem,
} from 'ui/DropdownItems/types';
import {
    getFormattedBpsOptions,
    getResourceTranslation,
    setAvailableResourcesForBps,
    sortBpsOptions,
} from '../../DuplicateSettings/helpers';
import { useGetArguments } from '../hooks';
import { BudgetBys } from 'business/constants';

type Props = {
    selectedSourceBudgetProposal: AutocompletAble<BudgetProposalWithResourcesT> | null;
    setSelectedSourceBudgetProposal: DispatchState<AutocompletAble<BudgetProposalWithResourcesT> | null>;
    selectedSourceResourceSoftwareId: AutocompletAble<string> | null;
    setSelectedSourceResourceSoftwareId: DispatchState<AutocompletAble<string> | null>;
    selectedArgumentIds: AutocompletAble<number>[] | null;
    setSelectedArgumentIds: DispatchState<AutocompletAble<number>[] | null>;
    budgetProposals: BudgetProposalWithResourcesT[];
    by: BudgetBys;
};

export default function FirstDuplicateArgumentsStep({
    selectedSourceBudgetProposal,
    setSelectedSourceBudgetProposal,
    selectedSourceResourceSoftwareId,
    setSelectedSourceResourceSoftwareId,
    selectedArgumentIds,
    setSelectedArgumentIds,
    budgetProposals,
    by: selectedBy,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const availableBPs = getFormattedBpsOptions(budgetProposals);
    const [availableResources, setAvailableResources] = useState<
        AutocompletAble<string>[]
    >([]);

    const [availableArguments, setAvailableArguments] = useState<
        AutocompletAble<number>[]
    >([]);

    useEffect(() => {
        setAvailableResourcesForBps(
            budgetProposals,
            selectedSourceBudgetProposal
                ? [selectedSourceBudgetProposal]
                : null,
            setAvailableResources,
            true,
        );
    }, [selectedSourceBudgetProposal]);

    const { data: argumentList, isLoading: isArgumentListLoading } =
        useGetArguments(
            selectedSourceBudgetProposal?.value?.id,
            selectedSourceResourceSoftwareId?.value,
        );

    useEffect(() => {
        if (argumentList && !isArgumentListLoading) {
            const options = argumentList.map((a) =>
                formatValueForAutocomplete(a.id, false, a.title),
            );
            setAvailableArguments(options);
        }
    }, [argumentList, isArgumentListLoading]);

    const autocompletes: Array<
        ModalFormItem<string | BudgetProposalWithResourcesT | number>
    > = [
        {
            children: (
                <GenericAutocomplete
                    selectedValues={selectedSourceBudgetProposal}
                    availableValues={availableBPs}
                    setSelectedValues={setSelectedSourceBudgetProposal}
                    label={t('adminBudgetSpace.duplicateArguments.sourceBps')}
                    shouldSortOptions
                    sortItems={sortBpsOptions}
                    key="selectedSourceBudgetProposal"
                />
            ),
            value: selectedSourceBudgetProposal,
            setValue: setSelectedSourceBudgetProposal,
        },
        {
            children: (
                <GenericAutocomplete
                    selectedValues={selectedSourceResourceSoftwareId}
                    availableValues={availableResources}
                    setSelectedValues={setSelectedSourceResourceSoftwareId}
                    label={capitalize(
                        t(
                            'adminBudgetSpace.duplicateArguments.sourceResource',
                            {
                                resource: getResourceTranslation(selectedBy),
                            },
                        ),
                    )}
                    shouldSortOptions
                    key="selectedSourceResourceSoftwareId"
                />
            ),
            value: selectedSourceResourceSoftwareId,
            setValue: setSelectedSourceResourceSoftwareId,
        },
        {
            children: (
                <GenericAutocomplete
                    selectedValues={selectedArgumentIds}
                    availableValues={availableArguments}
                    setSelectedValues={setSelectedArgumentIds}
                    label={t(
                        'adminBudgetSpace.duplicateArguments.sourceArgument',
                    )}
                    multiple
                    shouldSortOptions
                    key="selectedArgumentIds"
                />
            ),
            value: selectedArgumentIds,
            setValue: setSelectedArgumentIds,
        },
    ];

    return (
        <>
            <Typography variant="h4" sx={{ paddingBottom: '1.5em' }}>
                {t('adminBudgetSpace.duplicateArguments.title')}
            </Typography>
            {t('adminBudgetSpace.duplicateArguments.sourceText')}
            <ResetWrapper items={autocompletes} />
        </>
    );
}
